import React, { useState, useEffect } from 'react';
import axios from 'axios';
import MarkdownContent from './MarkdownContent';
import './Journal.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const Journal = () => {
  const [entries, setEntries] = useState([]);
  const [hobbyEntries, setHobbyEntries] = useState([]);
  const [aboutMe, setAboutMe] = useState('');
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('about');

  useEffect(() => {
    fetchEntries();
    fetchHobbyEntries();
    fetchAboutMe();
    fetchProfile();
  }, []);

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API_URL}/entries`);
      setEntries(response.data);
    } catch (error) {
      console.error('Error fetching entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHobbyEntries = async () => {
    try {
      const response = await axios.get(`${API_URL}/hobbies`);
      setHobbyEntries(response.data);
    } catch (error) {
      console.error('Error fetching hobby entries:', error);
    }
  };

  const fetchAboutMe = async () => {
    try {
      const response = await axios.get(`${API_URL}/about`);
      setAboutMe(response.data.content || '');
    } catch (error) {
      console.error('Error fetching about me:', error);
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/profile`);
      setProfile(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
      setProfile({});
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const imageUrl = profile?.image_url || null;
  const coverUrl = profile?.cover_url || null;
  const email = profile?.email || 'Test@gmail.com';
  const linkedin = profile?.linkedin_url || 'https://www.linkedin.com/in/mohamed-abushaqra-8067811b9/';
  const github = profile?.github_url || 'https://github.com/MohamedAbushaqra';
  const hackster = profile?.hackster_url || 'https://www.hackster.io/mabushaqraedu0';

  if (loading) {
    return (
      <div className="journal">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="journal">
      <header className="journal-header">
        <nav className="journal-nav">
          <div className="journal-brand">
            <span className="journal-logo">Your window to my world!</span>
            <span className="journal-tagline">Thoughts, ideas, and more</span>
          </div>
          <div className="nav-tabs">
            <button
              className={`nav-tab ${activeTab === 'about' ? 'active' : ''}`}
              onClick={() => setActiveTab('about')}
            >
              About Me
            </button>
            <button
              className={`nav-tab ${activeTab === 'entries' ? 'active' : ''}`}
              onClick={() => setActiveTab('entries')}
            >
              Journal
            </button>
            <button
              className={`nav-tab ${activeTab === 'hobbies' ? 'active' : ''}`}
              onClick={() => setActiveTab('hobbies')}
            >
              Hobbies
            </button>
            <button
              className={`nav-tab ${activeTab === 'contact' ? 'active' : ''}`}
              onClick={() => setActiveTab('contact')}
            >
              Contact
            </button>
          </div>
        </nav>
      </header>

      <main className="journal-main">
        <div className="journal-container">
          {activeTab === 'about' && (
            <div className="about-landing">
              <section className="hero">
                {coverUrl && (
                  <div
                    className="hero-cover"
                    style={{ backgroundImage: `url(${coverUrl})` }}
                    aria-hidden
                  />
                )}
                <div className="hero-content">
                  <div className="profile-photo-wrap">
                    {imageUrl ? (
                      <img src={imageUrl} alt={profile?.name || 'Profile'} className="profile-photo" />
                    ) : (
                      <div className="profile-photo-placeholder">
                        <span>{(profile?.name || 'You').charAt(0)}</span>
                      </div>
                    )}
                  </div>
                  <div className="hero-info-box">
                    <h1 className="hero-name">{profile?.name || 'Your Name'}</h1>
                    <p className="hero-tagline">{profile?.tagline || 'Your tagline or title'}</p>
                  </div>
                </div>
              </section>

              <section className="about-section card-mod">
                <h2>About Me</h2>
                <div className="entry-content">
                  {aboutMe ? <MarkdownContent content={aboutMe} /> : <p>No information available yet.</p>}
                </div>
              </section>

              <section className="about-contact-preview">
                <p>Get in touch — see the <button type="button" className="link-button" onClick={() => setActiveTab('contact')}>Contact</button> tab for links.</p>
              </section>
            </div>
          )}

          {activeTab === 'entries' && (
            <div className="entries-section">
              <h2 className="section-title">Journal Entries</h2>
              {entries.length === 0 ? (
                <div className="empty-state card-mod">
                  <h3>No entries yet</h3>
                  <p>Check back soon for new journal entries!</p>
                </div>
              ) : (
                <div className="entries-list">
                  {entries.map((entry) => (
                    <article key={entry.id} className="card-mod entry-card">
                      <div className="entry-date">{formatDate(entry.created_at)}</div>
                      <h2 className="entry-title">{entry.title}</h2>
                      <div className="entry-content"><MarkdownContent content={entry.content} /></div>
                    </article>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'hobbies' && (
            <div className="entries-section">
              <h2 className="section-title">Hobbies &amp; Interests</h2>
              {hobbyEntries.length === 0 ? (
                <div className="empty-state card-mod">
                  <h3>No hobby entries yet</h3>
                  <p>Check back soon for hobbies and interests!</p>
                </div>
              ) : (
                <div className="entries-list">
                  {hobbyEntries.map((entry) => (
                    <article key={entry.id} className="card-mod entry-card">
                      <div className="entry-date">{formatDate(entry.created_at)}</div>
                      <h2 className="entry-title">{entry.title}</h2>
                      <div className="entry-content"><MarkdownContent content={entry.content} /></div>
                    </article>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="contact-section card-mod">
              <h2>Contact Me</h2>
              <p className="contact-intro">I am happy to answer your personal requests and inquiries. Feel free to reach out:</p>
              <div className="contact-grid">
                <div className="contact-item">
                  <span className="contact-label">Email</span>
                  <a href={`mailto:${email}`} className="contact-link">{email}</a>
                </div>
                <div className="contact-item">
                  <span className="contact-label">LinkedIn</span>
                  <a href={linkedin} target="_blank" rel="noopener noreferrer" className="contact-link">Profile</a>
                </div>
                <div className="contact-item">
                  <span className="contact-label">GitHub</span>
                  <a href={github} target="_blank" rel="noopener noreferrer" className="contact-link">Profile</a>
                </div>
                <div className="contact-item">
                  <span className="contact-label">Hackster.io</span>
                  <a href={hackster} target="_blank" rel="noopener noreferrer" className="contact-link">Profile</a>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Journal;
